import { useCallback } from "react";
import { Transaction } from "@/context/FinanceContext";
import { useToast } from "@/hooks/use-toast";
import { useAuditLog } from "@/hooks/useAuditLog";
import { useSecurityAlerts } from "@/hooks/useSecurityAlerts";
import { useFinance } from "@/context/FinanceContext";

interface BulkActionsHookProps {
  updateTransaction: (id: string, updates: Partial<Transaction>) => void;
  removeTransaction: (id: string) => void;
  transactions: Transaction[];
}

/**
 * Custom hook for managing bulk transaction actions
 */
export function useTransactionBulkActions({
  updateTransaction,
  removeTransaction,
  transactions
}: BulkActionsHookProps) {
  const { toast } = useToast();
  const { logAction } = useAuditLog();
  const { detectBulkDelete } = useSecurityAlerts();
  const { bulkRemoveTransactions, bulkUpdateTransactions } = useFinance();

  const handleBulkEdit = useCallback(async (
    selectedIds: Set<string>,
    updates: Partial<Transaction>,
    onComplete?: () => void
  ) => {
    const ids = Array.from(selectedIds);
    await logAction('transactions', 'BULK_UPDATE', undefined, ids);

    try {
      // Aguarda a confirmação do banco antes de anunciar sucesso
      await bulkUpdateTransactions(ids, updates);
      toast({
        title: "Edição em lote aplicada",
        description: `${ids.length} lançamentos foram atualizados.`,
        duration: 1500,
      });
      onComplete?.();
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Erro na edição em lote",
        description: e?.message || "Não foi possível salvar as alterações.",
        duration: 6000,
      });
    }
  }, [bulkUpdateTransactions, toast, logAction]);

  const validateBulkDelete = useCallback((
    selectedIds: Set<string>
  ): { canDelete: boolean; paidCount: number } => {
    const selectedTransactions = transactions.filter(t => selectedIds.has(t.id));
    const reconciledTransactions = selectedTransactions.filter(t => t.reconciled === true);
    if (reconciledTransactions.length > 0) {
      toast({
        variant: "destructive",
        title: "Exclusão bloqueada",
        description: `${reconciledTransactions.length} lançamento(s) selecionado(s) está(ão) conciliado(s). Desfaça a conciliação antes de excluir.`,
        duration: 5000,
      });
      return { canDelete: false, paidCount: reconciledTransactions.length };
    }

    const paidTransactions = selectedTransactions.filter(t => t.settled === true);
    if (paidTransactions.length > 0) {
      toast({
        variant: "destructive",
        title: "Exclusão bloqueada",
        description: `${paidTransactions.length} lançamento(s) selecionado(s) está(ão) pago(s). Estorne o pagamento antes de excluir.`,
        duration: 5000,
      });
      return { canDelete: false, paidCount: paidTransactions.length };
    }

    return { canDelete: true, paidCount: 0 };
  }, [transactions, toast]);

  /**
   * Executes bulk delete - AGUARDA confirmação do Supabase antes do toast de sucesso
   */
  const executeBulkDelete = useCallback(async (
    selectedIds: Set<string>,
    onComplete?: () => void
  ) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    detectBulkDelete(ids.length);
    await logAction('transactions', 'BULK_DELETE', undefined, ids);

    const loadingToast = toast({
      title: "Excluindo lançamentos...",
      description: `Processando ${ids.length} lançamento(s). Aguarde.`,
      duration: 60000,
    });

    try {
      const result = await bulkRemoveTransactions(ids);

      if (result.failed > 0) {
        toast({
          variant: "destructive",
          title: "Exclusão parcial",
          description: `${result.deleted} excluído(s), ${result.failed} falharam. ${result.errors[0] || ''}`.trim(),
          duration: 8000,
        });
      } else {
        toast({
          title: "Lançamentos excluídos",
          description: `${result.deleted} lançamento(s) excluído(s) com sucesso.`,
        });
      }
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Erro ao excluir",
        description: e?.message || "Não foi possível excluir os lançamentos.",
        duration: 8000,
      });
    } finally {
      loadingToast.dismiss?.();
      onComplete?.();
    }
  }, [bulkRemoveTransactions, toast, detectBulkDelete, logAction]);

  const canDelete = useCallback((transaction: Transaction): boolean => {
    if (transaction.reconciled === true) {
      toast({
        variant: "destructive",
        title: "Operação cancelada",
        description: "Não é possível excluir lançamentos conciliados. Desfaça a conciliação primeiro.",
        duration: 5000,
      });
      return false;
    }

    if (transaction.settled === true) {
      toast({
        variant: "destructive",
        title: "Operação cancelada",
        description: "Estorne o pagamento deste lançamento antes de excluir.",
        duration: 5000,
      });
      return false;
    }

    return true;
  }, [toast]);

  return {
    handleBulkEdit,
    validateBulkDelete,
    executeBulkDelete,
    canDelete
  };
}
